import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  Products_Orders_SwitchTypeResponse,
} from '@monorepo-types/dc';
import { assign, setup } from 'xstate';

import { actors } from './sendOrder.actor';
import { FlowConsumerContextEnergy as FlowContext } from '../../../context';
import type { OfferResponse } from '../../types';

const isUnauthorizedError = (event: unknown) =>
  (event as { error?: { message?: string } })?.error?.message === 'Unauthorized';

// Shared onError transitions for the SEND and SEND_EXTENSION states below. Loosely typed to match
// the machine's own `: any` typing, since it's defined outside the `setup().createMachine()` call
// where XState would otherwise infer the context/event types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sendOrderErrorHandlers: any = [
  {
    target: 'ERROR_NOT_AUTHENTICATED',
    guard: ({ event }: { event: unknown }) => isUnauthorizedError(event),
    actions: assign(({ context }: { context: Record<string, unknown> }) => ({
      ...context,
      errorMessage: 'not authenticated',
    })),
  },
  {
    target: 'ERROR',
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sendOrderMachine: any = setup({
  types: {} as {
    input: {
      businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
      label: DC_Repositories_Base_Enumerations_Label;
    };
    context: {
      businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
      label: DC_Repositories_Base_Enumerations_Label;
      errorMessage?: string;
      flowContext: FlowContext;
      offersData: OfferResponse;
      switchTypes?: Array<Products_Orders_SwitchTypeResponse>;
    };
    events:
      | {
          type: 'SEND_ORDER';
          values: FlowContext;
          offers: OfferResponse;
        }
      | {
          type: 'SEND_EXTENSION_ORDER';
          values: FlowContext;
          offers: OfferResponse;
        };
  },
  actors,
}).createMachine({
  id: 'sendOrder',
  initial: 'IDLE',
  context: ({ input }) => ({
    businessUnit: input.businessUnit,
    label: input.label,
    flowContext: {} as FlowContext,
    offersData: {} as OfferResponse,
  }),
  states: {
    IDLE: {
      on: {
        SEND_ORDER: {
          target: 'SEND',
          actions: assign(({ context, event }) => ({
            ...context,
            flowContext: event.values,
            offersData: event.offers,
          })),
        },
        SEND_EXTENSION_ORDER: {
          target: 'SEND_EXTENSION',
          actions: assign(({ context, event }) => ({
            ...context,
            flowContext: event.values,
            offersData: event.offers,
          })),
        },
      },
    },
    SEND: {
      invoke: {
        src: 'sendProductOrder',
        input: ({ context }) => {
          return {
            businessUnit: context.businessUnit,
            label: context.label,
            flowContext: context.flowContext,
            offersData: context.offersData,
          };
        },
        onDone: {
          target: 'SUCCESS',
          actions: assign(({ context, event }) => ({
            ...context,
            switchTypes: event.output.data?.orderTypes ?? [],
          })),
        },
        onError: sendOrderErrorHandlers,
      },
    },
    SEND_EXTENSION: {
      invoke: {
        src: 'sendProductExtensionOrder',
        input: ({ context }) => {
          return {
            businessUnit: context.businessUnit,
            label: context.label,
            flowContext: context.flowContext,
            offersData: context.offersData,
          };
        },
        onDone: {
          target: 'SUCCESS',
          actions: assign(({ context, event }) => ({
            ...context,
            switchTypes: event.output.data?.orderTypes ?? [],
          })),
        },
        onError: sendOrderErrorHandlers,
      },
    },
    SUCCESS: {
      type: 'final',
    },
    ERROR: {},
    ERROR_NOT_AUTHENTICATED: {
      type: 'final',
    },
  },
});
